import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Modal from '../Modal';
import {
  LEARNERS_PAGE_SIZE,
  specError,
  useBulkApprove,
  useLearners,
} from '../../hooks/useSpecialization';
import {
  CARD,
  INPUT,
  PRIMARY,
  SECONDARY,
  formatPercent,
  plural,
  ruleLabel,
} from './specText';

const FILTERS = [
  { key: '', label: 'All', count: 'members' },
  {
    key: 'awaiting_approval',
    label: 'Awaiting approval',
    count: 'awaiting_approval',
  },
  { key: 'qualified', label: 'Qualified', count: 'qualified' },
  { key: 'not_qualified', label: 'Not qualified', count: 'not_qualified' },
  { key: 'chosen', label: 'Chosen', count: 'chosen' },
];

const ruleDetail = (rule) => {
  if (rule.type !== 'manual_approval') {
    return `${formatPercent(rule.current)} of ${formatPercent(rule.target)}`;
  }
  return rule.met ? 'approved' : 'not approved';
};

function RuleDots({ rules }) {
  if (!rules?.length) return <span className="text-xs text-gray-400">–</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {rules.map((rule) => (
        <span
          key={rule.type}
          title={`${ruleLabel(rule.type)}: ${ruleDetail(rule)}`}
          className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] ${
            rule.met
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {rule.met ? (
            <CheckIcon sx={{ fontSize: 11 }} />
          ) : (
            <CloseIcon sx={{ fontSize: 11 }} />
          )}
          {rule.type === 'manual_approval'
            ? 'Approval'
            : `${ruleLabel(rule.type)} ${formatPercent(rule.current)}`}
        </span>
      ))}
    </div>
  );
}

function BulkApproveDialog({ open, count, onConfirm, onClose, loading }) {
  return (
    <Modal modalOpen={open} onClose={onClose} title="Approve learners?" small>
      <div className="space-y-4 text-left">
        <p className="text-sm text-gray-700">
          Approve {plural(count, 'learner')} for the manual approval rule. It
          takes effect at once; learners who meet every other rule can then
          choose a specialization.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={PRIMARY}
          >
            {loading ? 'Approving…' : `Approve ${count}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function LearnersPanel({ cohortId, onOpen }) {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [skipped, setSkipped] = useState([]);
  const bulk = useBulkApprove(cohortId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, refetch } = useLearners(
    cohortId,
    { status, q },
    offset
  );
  const students = data?.students ?? [];
  const total = data?.total ?? 0;
  const counts = data?.counts ?? {};
  // Rows carry the saved rules, so this works without the settings permission.
  const approvalRule = students.some((s) =>
    s.rules?.some((r) => r.type === 'manual_approval')
  );

  // Approval only matters with the manual approval rule; only learners not
  // yet approved can be selected.
  const selectable = approvalRule
    ? students.filter((s) => !s.approval?.approved)
    : [];
  const allSelected =
    selectable.length > 0 &&
    selectable.every((s) => selected.has(s.student_id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      selectable.forEach((s) =>
        allSelected ? next.delete(s.student_id) : next.add(s.student_id)
      );
      return next;
    });

  const approve = () =>
    bulk.mutate([...selected], {
      onSuccess: (result) => {
        setConfirming(false);
        setSelected(new Set());
        setSkipped(result.skipped);
        if (result.approved.length) {
          toast.success(
            `Approved ${plural(result.approved.length, 'learner')}.`
          );
        }
        if (result.skipped.length) {
          toast.error(
            `${plural(
              result.skipped.length,
              'learner was',
              'learners were'
            )} not approved.`
          );
        }
      },
      onError: (err) => {
        setConfirming(false);
        toast.error(specError(err, "We couldn't approve these learners."));
      },
    });

  if (error && !data) {
    return (
      <div className={`${CARD} p-8 text-center space-y-3`}>
        <p className="text-sm text-gray-600">
          {specError(error, "We couldn't load the learners.")}
        </p>
        {error?.response?.status !== 403 && (
          <button type="button" onClick={() => refetch()} className={PRIMARY}>
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data && !data.gated && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          Qualification is off for this cohort. This shows how learners measure
          against the saved settings.
        </div>
      )}

      <section className={`${CARD} p-4 space-y-3`}>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter.key || 'all'}
              type="button"
              aria-pressed={status === filter.key}
              onClick={() => {
                setStatus(filter.key);
                setOffset(0);
                setSelected(new Set());
              }}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
                status === filter.key
                  ? 'border-claret-500 bg-claret-500 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {filter.label}
              {counts[filter.count] !== undefined && (
                <span
                  className={`rounded-full px-1.5 text-[10px] font-semibold ${
                    status === filter.key
                      ? 'bg-white/20'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {counts[filter.count].toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <input
            type="search"
            aria-label="Search learners"
            placeholder="Search name, email or student ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${INPUT} w-72`}
          />
          {counts.enrolled_without_choice > 0 && (
            <p className="text-xs text-gray-500">
              {plural(counts.enrolled_without_choice, 'learner')}{' '}
              {counts.enrolled_without_choice === 1 ? 'has' : 'have'}{' '}
              specialization modules without a choice.
            </p>
          )}
          {approvalRule && selected.size > 0 && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className={PRIMARY}
            >
              Approve {selected.size}…
            </button>
          )}
        </div>
      </section>

      {skipped.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-amber-800">Not approved</p>
            <button
              type="button"
              onClick={() => setSkipped([])}
              className="text-xs text-amber-800 hover:underline"
            >
              Dismiss
            </button>
          </div>
          <ul className="m-0 list-none space-y-0.5 p-0">
            {skipped.map((item) => (
              <li key={item.student_id} className="text-xs text-amber-800">
                {students.find((s) => s.student_id === item.student_id)?.name ||
                  item.student_id}
                : {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading && (
        <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
      )}
      {!isLoading && total === 0 && (
        <div className={`${CARD} p-10 text-center`}>
          <p className="text-sm text-gray-600">No learners match.</p>
        </div>
      )}
      {!isLoading && total > 0 && (
        <section className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  {approvalRule && (
                    <th className="w-8 py-2 pl-5">
                      <input
                        type="checkbox"
                        aria-label="Select all learners on this page awaiting approval"
                        checked={allSelected}
                        disabled={!selectable.length}
                        onChange={toggleAll}
                      />
                    </th>
                  )}
                  <th
                    className={`py-2 pr-2 font-medium ${
                      approvalRule ? 'pl-2' : 'pl-5'
                    }`}
                  >
                    Learner
                  </th>
                  <th className="px-2 py-2 font-medium">Rules</th>
                  <th className="px-2 py-2 font-medium">Qualified</th>
                  <th className="px-2 py-2 font-medium">Specialization</th>
                  <th className="py-2 pl-2 pr-5" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr
                    key={student.student_id}
                    onClick={() => onOpen(student)}
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    {approvalRule && (
                      // The checkbox mustn't open the learner.
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                      <td
                        className="py-2.5 pl-5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label={`Select ${student.name || student.email}`}
                          checked={selected.has(student.student_id)}
                          disabled={student.approval?.approved}
                          onChange={() => toggle(student.student_id)}
                        />
                      </td>
                    )}
                    <td
                      className={`py-2.5 pr-2 ${
                        approvalRule ? 'pl-2' : 'pl-5'
                      }`}
                    >
                      <span className="text-gray-900">
                        {student.name || '–'}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {student.email}
                      </span>
                    </td>
                    <td className="px-2 py-2.5">
                      <RuleDots rules={student.rules} />
                    </td>
                    <td className="px-2 py-2.5 text-xs">
                      {student.qualified && (
                        <span className="text-green-700">Yes</span>
                      )}
                      {!student.qualified && student.awaiting_approval && (
                        <span className="text-amber-700">
                          Awaiting approval
                        </span>
                      )}
                      {!student.qualified && !student.awaiting_approval && (
                        <span className="text-gray-500">Not yet</span>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-gray-700">
                      {student.selections?.length
                        ? student.selections
                            .map((s) => s.track_name || s.track_id)
                            .join(', ')
                        : '–'}
                      {student.enrolled_without_choice?.length > 0 && (
                        <span className="block text-gray-500">
                          {plural(
                            student.enrolled_without_choice.length,
                            'module'
                          )}{' '}
                          without a choice
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pl-2 pr-5 text-right text-gray-400">
                      <ChevronRightIcon fontSize="small" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
            <span>
              {(offset + 1).toLocaleString()}–
              {Math.min(offset + students.length, total).toLocaleString()} of{' '}
              {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() =>
                  setOffset(Math.max(0, offset - LEARNERS_PAGE_SIZE))
                }
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={offset + LEARNERS_PAGE_SIZE >= total}
                onClick={() => setOffset(offset + LEARNERS_PAGE_SIZE)}
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}

      <BulkApproveDialog
        open={confirming}
        count={selected.size}
        loading={bulk.isLoading}
        onConfirm={approve}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
